import SkeletonTable from "@/components/SkeletonTable"
import { Badge } from "@/components/ui/badge"
import api from "@/lib/axios"
import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import { FaArrowLeftLong, FaFaceGrinBeam } from "react-icons/fa6"

type DataKepemimpinanPublic = {
  id_data_pendaftaran: number;
  nomor_pendaftaran: string;
  nisn: string;
  nama_lengkap: string;
  handphone: string;
  path_foto: string;
  npsn_sekolah: string;
  nama_sekolah: string;
  pilihan1: string;
  pilihan2: string;
  pilihan3: string;
  pilihan4: string;

  id_data_kepemimpinan: number;
  nama_organisasi: string;
  lingkup: string;
  jabatan: string;
  nomor_sk: string;
  tahun_sk: string;
  file_sk: string;

  user_ver1: number | null;
  validasi_ver1: number | null;
  jabatan_ver1: string | null;
  nilai_ver1: number | null;
  ket_ver1: string | null;
  nama_ver1: string | null;

  user_ver2: number | null;
  validasi_ver2: number | null;
  jabatan_ver2: string | null;
  nilai_ver2: number | null;
  ket_ver2: string | null;
  nama_ver2: string | null;

  user_ver3: number | null;
  validasi_ver3: number | null;
  jabatan_ver3: string | null;
  nilai_ver3: number | null;
  ket_ver3: string | null;
  nama_ver3: string | null;
}

const getDetailNilaiKepemimpinan = async ({
  token,
  nomor_pendaftaran,
}: {
  token: string
  nomor_pendaftaran: string
}) => {
  const response = await api.get(
    `/nilai-kepemimpinan-smbjm-public?token=${token}&nomor_pendaftaran=${nomor_pendaftaran}`
  )
  return response.data?.data
}

export default function DetailNilaiKepemimpinanSmbjmPublicPage() {
  const [searchParams] = useSearchParams()
  const tokenParams = searchParams.get("token") || ""
  const nomorPendaftaranParams = searchParams.get("nomor_pendaftaran") || ""

  const { data, isPending } = useQuery<DataKepemimpinanPublic>({
    queryKey: ["detailNilaiKepemimpinan"],
    queryFn: () =>
      getDetailNilaiKepemimpinan({
        token: tokenParams,
        nomor_pendaftaran: nomorPendaftaranParams,
      }),
    enabled: !!tokenParams && !!nomorPendaftaranParams,
  })

  if (isPending) return <SkeletonTable />

  if (!data)
    return (
      <div className="h-screen flex justify-center items-center">
        <h1 className="font-bold text-2xl">Data tidak ditemukan!</h1>
      </div>
    )

  const verifikatorKe = 1
  const keyJabatan = `jabatan_ver${verifikatorKe}` as keyof DataKepemimpinanPublic
  const keyKet = `ket_ver${verifikatorKe}` as keyof DataKepemimpinanPublic
  const keyNilai = `nilai_ver${verifikatorKe}` as keyof DataKepemimpinanPublic
  const keyValidasi = `validasi_ver${verifikatorKe}` as keyof DataKepemimpinanPublic
  const keyNamaVerifikator = `nama_ver${verifikatorKe}` as keyof DataKepemimpinanPublic

  return (
    <div>
      <div className="relative p-5 flex lg:flex-row flex-col gap-3 h-[90vh] overflow-auto">
        <section className="p-5 lg:w-[40%] border rounded-xl overflow-auto">
          <div className="mb-10">
            <table>
              <thead>
                <tr>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-2 py-1 font-semibold">Nomor Pendaftaran</td>
                  <td className="px-2 py-1">:</td>
                  <td className="px-2 py-1">
                    {data?.nomor_pendaftaran}
                  </td>
                </tr>
                <tr>
                  <td className="px-2 py-1 font-semibold">Nama</td>
                  <td className="px-2 py-1">:</td>
                  <td className="px-2 py-1">
                    {data?.nama_lengkap}
                  </td>
                </tr>
                <tr>
                  <td className="px-2 py-1 font-semibold align-top">Prodi Pilihan</td>
                  <td className="px-2 py-1 align-top">:</td>
                  <td className="px-2 py-1">
                    <ul>
                      {data?.pilihan1 && <li>1. {data.pilihan1}</li>}
                      {data?.pilihan2 && <li>2. {data.pilihan2}</li>}
                      {data?.pilihan3 && <li>3. {data.pilihan3}</li>}
                      {data?.pilihan4 && <li>4. {data.pilihan4}</li>}
                    </ul>
                  </td>
                </tr>
                <tr>
                  <td className="px-2 py-1 font-semibold">NISN</td>
                  <td className="px-2 py-1">:</td>
                  <td className="px-2 py-1">{data?.nisn}</td>
                </tr>
                <tr>
                  <td className="px-2 py-1 font-semibold">No Handphone</td>
                  <td className="px-2 py-1">:</td>
                  <td className="px-2 py-1">
                    {data?.handphone}
                  </td>
                </tr>
                <tr>
                  <td className="px-2 py-1 font-semibold">Sekolah</td>
                  <td className="px-2 py-1">:</td>
                  <td className="px-2 py-1">
                    {data?.nama_sekolah} | {data?.npsn_sekolah}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex-1 overflow-auto border rounded-xl">
            <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
              <thead>
                <tr className="bg-secondary">
                  <th className="px-2 py-1 font-semibold border" rowSpan={2}>
                    Organisasi
                  </th>
                  <th className="px-2 py-1 font-semibold border" rowSpan={2}>
                    Jabatan (Peserta)
                  </th>
                  <th className="px-2 py-1 font-semibold border" colSpan={4}>
                    Verifikator
                  </th>
                </tr>
                <tr>
                  <th className="px-2 py-1 font-semibold border">
                    Jabatan
                  </th>
                  <th className="px-2 py-1 font-semibold border">
                    Nilai
                  </th>
                  <th className="px-2 py-1 font-semibold border">Status</th>
                  <th className="px-2 py-1 font-semibold border">Keterangan</th>
                </tr>
              </thead>
              <tbody>
                {data?.id_data_kepemimpinan ? (
                  <tr>
                    <td className="px-2 py-1 border text-center">
                      {data.nama_organisasi}
                    </td>
                    <td className="px-2 py-1 border text-center">
                      {data.jabatan}
                    </td>
                    <td className="px-2 py-1 border text-center">
                      {data[keyJabatan] as string}
                    </td>
                    <td className="px-2 py-1 border text-center">
                      {data[keyNilai] as number}
                    </td>
                    <td className="px-2 py-1 border text-center">
                      {(() => {
                        const value = data[keyValidasi]

                        if (value === 0 || value === null)
                          return (
                            <Badge variant={"secondary"}>
                              Belum diverifikasi
                            </Badge>
                          )
                        if (value === 1)
                          return (
                            <Badge variant={"success"}>Valid</Badge>
                          )
                        if (value === 2)
                          return (
                            <Badge variant={"destructive"}>
                              Tidak Valid
                            </Badge>
                          )

                        return ""
                      })()}
                      <p className="text-sm italic">
                        {data[keyNamaVerifikator] as string}
                      </p>
                    </td>
                    <td className="px-2 py-1 border text-center">
                      {data[keyKet] as string}
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={6} className="px-2 py-4 border text-center">
                      Data nilai kepemimpinan tidak ditemukan...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        <section className="relative overflow-auto flex-1 border rounded-xl">
          <div className="p-5 pb-0 flex items-center gap-5">
            {data?.file_sk ? (
              <div className="flex-1 text-xl font-semibold">
                <div className="flex-1 flex flex-wrap justify-between font-bold text-xl">
                  <div>
                    Berkas SK{" "}
                    <span className="text-primary">
                      {data.nama_organisasi}
                    </span>{" "}
                    <span>
                      Jabatan{" "}
                      <Badge className="text-xl">
                        {data.jabatan}
                      </Badge>
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              ""
            )}
          </div>
          {data?.file_sk ? (
            <div className="relative h-[80vh]">
              <iframe
                src={data.file_sk}
                className="p-5 absolute w-full h-full"
              />
            </div>
          ) : (
            <div className="h-[60vh] flex justify-center items-center">
              <div>
                <FaFaceGrinBeam size={50} className="mb-5 mx-auto" />
                <div className="text-2xl font-bold flex items-center gap-3">
                  <FaArrowLeftLong />
                  Pilih berkas untuk dilihat
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
